
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Users, 
  BookOpen, 
  Award, 
  TrendingUp, 
  UserCheck, 
  RefreshCw,
  Clock,
  BarChart3
} from 'lucide-react';
import { useStaffCapabilities } from '@/hooks/auth/useStaffCapabilities';
import StaffAnalyticsCharts from './StaffAnalyticsCharts';
import { staffAnalyticsDataService, type UserRoleData, type CategoryCompletion, type CertificateTrend } from '@/services/staffAnalyticsDataService';

const StaffAnalyticsDashboard: React.FC = () => {
  const { hasStaffAccess, analytics, loading, refreshAnalytics } = useStaffCapabilities();
  const [chartData, setChartData] = useState<{
    userRoles: UserRoleData | null;
    categories: CategoryCompletion[] | null;
    certificates: CertificateTrend[] | null;
  }>({
    userRoles: null,
    categories: null,
    certificates: null
  });

  useEffect(() => {
    if (hasStaffAccess) {
      loadChartData();
    }
  }, [hasStaffAccess]);

  const loadChartData = async () => {
    try {
      const [userRoles, categories, certificates] = await Promise.all([
        staffAnalyticsDataService.getUserRoleDistribution(),
        staffAnalyticsDataService.getCategoryCompletions(),
        staffAnalyticsDataService.getCertificateTrend()
      ]);

      setChartData({ userRoles, categories, certificates });
    } catch (error) {
      console.error('Error loading chart data:', error);
    }
  };

  if (!hasStaffAccess) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <h3 className="text-lg font-medium mb-2">Access Denied</h3>
          <p className="text-muted-foreground">You need Staff access to view this dashboard.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-blue-600" />
            Staff Analytics Dashboard
            {analytics?.staff_level_access && (
              <Badge variant="secondary" className="ml-2">Staff Level</Badge>
            )}
          </h2>
          <p className="text-muted-foreground">Overview of users, assignments, and achievements</p>
        </div>
        <Button onClick={refreshAnalytics} disabled={loading} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Analytics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold">{analytics?.total_users || 0}</p>
                <p className="text-xs text-muted-foreground">Manageable users</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Assignments</p>
                <p className="text-2xl font-bold">{analytics?.active_assignments || 0}</p>
                <p className="text-xs text-muted-foreground">In progress</p>
              </div>
              <BookOpen className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completed Assignments</p>
                <p className="text-2xl font-bold">{analytics?.completed_assignments || 0}</p>
                <p className="text-xs text-muted-foreground">Finished courses</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Certificates Issued</p>
                <p className="text-2xl font-bold">{analytics?.total_certificates || 0}</p>
                <p className="text-xs text-muted-foreground">Total achievements</p>
              </div>
              <Award className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Approvals</p>
                <p className="text-2xl font-bold">{analytics?.pending_approvals || 0}</p>
                <p className="text-xs text-muted-foreground">Awaiting review</p>
              </div>
              <UserCheck className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completion Rate</p>
                <p className="text-2xl font-bold">{Math.round(analytics?.completion_rate || 0)}%</p>
                <Progress value={analytics?.completion_rate || 0} className="h-2 mt-2" />
              </div>
              <Clock className="h-8 w-8 text-indigo-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Visual Analytics Charts */}
      {analytics && (
        <StaffAnalyticsCharts 
          analytics={analytics}
          userRoleData={chartData.userRoles || undefined}
          categoryData={chartData.categories || undefined}
          certificateTrend={chartData.certificates || undefined}
        />
      )}

      {/* Summary Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Analytics Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Overall Progress</span>
              <div className="flex items-center gap-2">
                <Progress value={analytics?.completion_rate || 0} className="w-24 h-2" />
                <span className="text-sm font-medium">{Math.round(analytics?.completion_rate || 0)}%</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <p className="text-sm text-muted-foreground">Active Learning</p>
                <p className="text-lg font-semibold">{analytics?.active_assignments || 0} ongoing</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Success Rate</p>
                <p className="text-lg font-semibold">
                  {analytics?.completed_assignments && analytics?.total_certificates
                    ? Math.round((analytics.total_certificates / analytics.completed_assignments) * 100)
                    : 0}% certified
                </p>
              </div>
            </div>

            {analytics?.generated_at && (
              <p className="text-xs text-muted-foreground pt-2 border-t flex items-center justify-center gap-2">
                <Clock className="h-4 w-4" />
                Last updated: {new Date(analytics.generated_at).toLocaleString()}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffAnalyticsDashboard;
